import {useCallback, useState} from 'react';
import {OrderCompletionGuidResult, OrderGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {orderAPI} from '../../../services/OrderServices';
import {getApiErrorMessage, normalizeApiError} from '../../../common/apiErrors';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';

const resultFromError = (error: unknown) => {
    const result = normalizeApiError(error).args?.result;
    return result && typeof result === 'object' && 'missingStock' in result ? result as OrderCompletionGuidResult : undefined;
};

export const useOrderCompletionGuid = (order?: OrderGuidDto) => {
    const {t} = useAppTranslation();
    const [complete, {isLoading}] = orderAPI.useCompleteOrderV2Mutation();
    const [preview, setPreview] = useState<OrderCompletionGuidResult>();
    const [errorMessage, setErrorMessage] = useState('');
    const simulateCompletion = useCallback(async () => {
        if (!order) return;
        setErrorMessage('');
        try { setPreview(await complete({id: order.id, simulate: true}).unwrap()); }
        catch (error) { const result = resultFromError(error); result ? setPreview(result) : setErrorMessage(getApiErrorMessage(error, t)); }
    }, [complete, order, t]);
    const confirmCompletion = useCallback(async () => {
        if (!order || !preview) return;
        setErrorMessage('');
        try {
            await complete({id: order.id, simulate: false, allowNegativeStock: preview.missingStock.length > 0}).unwrap();
            setPreview(undefined);
        } catch (error) { const result = resultFromError(error); result ? setPreview(result) : setErrorMessage(getApiErrorMessage(error, t)); }
    }, [complete, order, preview, t]);
    return {preview, errorMessage, isLoading, simulateCompletion, confirmCompletion, cancelCompletion: () => { setPreview(undefined); setErrorMessage(''); }};
};
